import { useMemo, useState } from 'react';
import { Printer, Heart, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';

const DEFAULT_FORM = {
  word: 'LOVE',
  recipient: '',
  from: 'Lindy',
  inspiration: 'Inspired by the quiet strength of a single word, hidden in plain sight.',
  description: 'A 19 cm handmade Morse code bracelet, strung on 7-strand steel wire.',
  gift_message: 'May this whisper its meaning to you on the days you most need to hear it.',
};

const todayUK = () =>
  new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

export default function KeepsakeCardPage() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const issuedOn = useMemo(() => todayUK(), []);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const handlePrint = () => window.print();

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="keepsake-card-page">
      <style>{`
        @media print {
          @page { size: A6 portrait; margin: 0; }
          html, body { background: #FAF9F6 !important; }
          body * { visibility: hidden; }
          .print-area, .print-area * {
            visibility: visible;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-area {
            position: absolute;
            left: 0 !important;
            top: 0 !important;
            transform: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
            width: 105mm !important;
            height: 148mm !important;
            page-break-after: always;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-10 no-print">
          <p className="text-caption text-[#84A98C] mb-4">Print &amp; Tuck Into the Box</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Keepsake Card
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Print an A6 keepsake card with the three little notes that travel with every bracelet — perfect for gifting.
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 items-start">
          {/* Card Preview (the actual printable area, scaled visually here) */}
          <div className="flex justify-center">
            <div
              className="print-area bg-[#FAF9F6] text-[#354F52] shadow-soft"
              style={{
                width: '105mm',
                height: '148mm',
                padding: '10mm',
                boxSizing: 'border-box',
                backgroundImage:
                  'radial-gradient(circle at 20% 0%, rgba(212,175,55,0.10), transparent 45%), radial-gradient(circle at 100% 100%, rgba(132,169,140,0.10), transparent 45%)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '6mm',
              }}
              data-testid="keepsake-card-preview"
            >
              {/* Top brand row */}
              <div style={{ textAlign: 'center' }}>
                <p style={{
                  fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
                  fontSize: '7pt',
                  letterSpacing: '0.35em',
                  color: '#84A98C',
                  marginBottom: '1mm',
                }}>WHISPERING WRISTWEAR</p>
                <p style={{
                  fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
                  fontSize: '20pt',
                  fontWeight: 500,
                  color: '#354F52',
                  lineHeight: 1.05,
                }}>
                  {form.word ? form.word.toUpperCase() : '—'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2mm' }}>
                  <MorseCodeVisualizer text={form.word || ''} size="sm" />
                </div>
              </div>

              {/* Three notes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4mm', fontSize: '8pt', lineHeight: 1.45 }}>
                <Section title="Inspiration" body={form.inspiration} />
                <Section title="The Bracelet" body={form.description} />
                <Section title="A Note to You" body={form.gift_message} />
              </div>

              {/* Spacer pushes signature down */}
              <div style={{ flex: 1 }} />

              {/* Recipient + signature */}
              <div style={{
                borderTop: '0.4mm solid rgba(132,169,140,0.4)',
                paddingTop: '3mm',
                textAlign: 'center',
                fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
              }}>
                {form.recipient && (
                  <p style={{ fontSize: '8pt', color: '#52796F', marginBottom: '1mm' }}>
                    For <span style={{ color: '#354F52', fontWeight: 500 }}>{form.recipient}</span>
                  </p>
                )}
                <p style={{ fontSize: '11pt', color: '#354F52' }}>
                  with love, <span style={{ color: '#D4AF37' }}>{form.from || 'Lindy'}</span>
                </p>
                <p style={{ fontSize: '6pt', letterSpacing: '0.25em', color: '#84A98C', marginTop: '1mm' }}>
                  {issuedOn.toUpperCase()} · PAPHOS, CYPRUS
                </p>
              </div>

              {/* Corner motif */}
              <div style={{ position: 'absolute', top: '6mm', right: '6mm', color: '#D4AF37', opacity: 0.7 }}>
                <Heart size={10} strokeWidth={1.25} />
              </div>
              <div style={{ position: 'absolute', bottom: '6mm', left: '6mm', color: '#84A98C', opacity: 0.6 }}>
                <Sparkles size={10} strokeWidth={1.25} />
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="no-print space-y-5 bg-[#F5F5F0] p-6 rounded-sm" data-testid="keepsake-form">
            <p className="text-caption text-[#84A98C]">Fill in the details</p>

            <div className="space-y-2">
              <Label htmlFor="word" className="text-[#354F52]">Bracelet word</Label>
              <Input
                id="word"
                value={form.word}
                onChange={set('word')}
                maxLength={30}
                className="uppercase tracking-widest border-[#CAD2C5]"
                data-testid="keepsake-word"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="recipient" className="text-[#354F52]">For (optional)</Label>
                <Input
                  id="recipient"
                  value={form.recipient}
                  onChange={set('recipient')}
                  maxLength={40}
                  placeholder="e.g. Sophie"
                  className="border-[#CAD2C5]"
                  data-testid="keepsake-recipient"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="from" className="text-[#354F52]">From</Label>
                <Input
                  id="from"
                  value={form.from}
                  onChange={set('from')}
                  maxLength={40}
                  className="border-[#CAD2C5]"
                  data-testid="keepsake-from"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inspiration" className="text-[#354F52]">Inspiration</Label>
              <Textarea
                id="inspiration"
                value={form.inspiration}
                onChange={set('inspiration')}
                rows={2}
                maxLength={180}
                className="border-[#CAD2C5]"
                data-testid="keepsake-inspiration"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-[#354F52]">The bracelet</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={set('description')}
                rows={2}
                maxLength={180}
                className="border-[#CAD2C5]"
                data-testid="keepsake-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gift_message" className="text-[#354F52]">A note to you</Label>
              <Textarea
                id="gift_message"
                value={form.gift_message}
                onChange={set('gift_message')}
                rows={3}
                maxLength={220}
                className="border-[#CAD2C5]"
                data-testid="keepsake-gift-message"
              />
            </div>

            <Button
              type="button"
              onClick={handlePrint}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="keepsake-print"
            >
              <Printer className="w-4 h-4" strokeWidth={1.5} />
              Print A6 keepsake card (PDF)
            </Button>
            <p className="text-xs text-[#84A98C] text-center">
              Tip: in the Print dialog, choose <strong>Save as PDF</strong> and set paper size to <strong>A6</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div>
      <p style={{
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
        fontSize: '6.5pt',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: '#84A98C',
        marginBottom: '0.8mm',
      }}>
        {title}
      </p>
      <p style={{
        fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
        fontSize: '8.5pt',
        color: '#354F52',
        fontStyle: 'italic',
      }}>
        {body || '—'}
      </p>
    </div>
  );
}
